import { useCallback, useEffect, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { readStoredAuthToken, readStoredUser } from "../../utils/sessionUser";
import { subscribeToCrossTabDataChanges } from "../../utils/crossTabDataSync";

export const PURCHASE_PRICE_PAGE_SIZE = 25;
const CACHE_TTL = 30_000;
const CACHE_LIMIT = 250;
const positiveId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
const sessionSignature = () => {
  const user = readStoredUser();
  return JSON.stringify([user?.id, user?.active_business_unit_id, user?.permissions, readStoredAuthToken()]);
};

export function purchasePriceLine(item, supplierId) {
  return {
    client_key: String(item.id), item_id: Number(item.id),
    supplier_id: positiveId(supplierId), unit_id: positiveId(item.unit_id),
  };
}

// Return one result per requested line, in request order, even if the API reorders them.
export async function fetchPurchasePriceBatch(payload, config) {
  const response = await axios.post("/api/purchasing/tenders-pricing/price-previews", payload, {
    ...config, skipCrossTabSync: true,
  });
  if (!Array.isArray(response.data?.lines)) throw new Error("Invalid price preview response");
  const results = new Map(response.data.lines.map((line) => [String(line.client_key), line]));
  return payload.lines.map((line) => {
    const result = results.get(line.client_key);
    if (Number(result?.item_id) === line.item_id) {
      if (result.source === "none" || result.source === "error") return result;
      const amount = result.unit_price;
      if (["tender", "last_purchase"].includes(result.source) &&
          (typeof amount === "number" || (typeof amount === "string" && amount.trim() !== "")) &&
          Number.isFinite(Number(amount)) && Number(amount) >= 0) return result;
    }
    return { item_id: line.item_id, source: "error" };
  });
}

/** Read-only, bounded price previews. Saving always resolves the authoritative price again. */
export default function usePurchasePricePreviews({
  items = [], businessUnitId, supplierId, date, currency = "EUR", enabled = true, contextKey = "", refreshKey = "",
}) {
  const cache = useRef(new Map());
  const cacheScope = useRef("");
  const generation = useRef(0);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({ key: "", prices: {}, loading: false, error: false });
  const identity = sessionSignature();
  const user = readStoredUser();
  const active = enabled && positiveId(businessUnitId) && positiveId(user?.id)
    && Number(user?.active_business_unit_id) === Number(businessUnitId);
  const seen = new Set();
  const lines = items.filter((item) => {
    const id = positiveId(item?.id);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  }).slice(0, PURCHASE_PRICE_PAGE_SIZE).map((item) => purchasePriceLine(item, supplierId));
  const scope = JSON.stringify([identity, businessUnitId, supplierId || null, date || null, currency, contextKey, refreshKey]);
  const request = JSON.stringify({ ...(date ? { date: String(date).slice(0, 10) } : {}), currency, lines });
  const key = JSON.stringify([scope, request, Boolean(active), revision]);
  const retry = useCallback(() => {
    generation.current += 1;
    cache.current.clear();
    setRevision((value) => value + 1);
  }, []);

  useEffect(() => {
    const onStorage = (event) => {
      if (!event.key || ["palantir_user", "palantir_token"].includes(event.key)) retry();
    };
    const onFocus = () => setRevision((value) => value + 1);
    window.addEventListener("palantir-session-updated", retry);
    window.addEventListener("storage", onStorage);
    window.addEventListener("purchasing-tenders-changed", retry);
    window.addEventListener("focus", onFocus);
    const unsubscribe = subscribeToCrossTabDataChanges((event) => {
      if (String(event.url || "").includes("/purchasing/")) retry();
    });
    return () => {
      window.removeEventListener("palantir-session-updated", retry);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("purchasing-tenders-changed", retry);
      window.removeEventListener("focus", onFocus);
      unsubscribe();
    };
  }, [retry]);

  useEffect(() => {
    const currentGeneration = ++generation.current;
    if (cacheScope.current !== scope) {
      cache.current.clear();
      cacheScope.current = scope;
    }
    const payload = JSON.parse(request);
    if (!active || !payload.lines.length) {
      setState({ key, prices: {}, loading: false, error: false });
      return undefined;
    }
    const prices = {};
    const pending = [];
    for (const line of payload.lines) {
      const cached = cache.current.get(JSON.stringify(line));
      if (cached && Date.now() - cached.time < CACHE_TTL) prices[line.item_id] = cached.price;
      else pending.push(line);
    }
    setState({ key, prices, loading: pending.length > 0, error: false });
    if (!pending.length) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const isCurrent = () => !cancelled && generation.current === currentGeneration && sessionSignature() === identity;
    const timer = setTimeout(async () => {
      try {
        const results = await fetchPurchasePriceBatch({ ...payload, lines: pending }, {
          signal: controller.signal,
          headers: { "X-Pilcore-Business-Unit-Id": String(businessUnitId) },
          pilcoreExpectedUserId: user.id,
        });
        if (!isCurrent()) return;
        pending.forEach((line, index) => {
          const price = results[index];
          prices[line.item_id] = price;
          if (price.source !== "error") cache.current.set(JSON.stringify(line), { time: Date.now(), price });
        });
        while (cache.current.size > CACHE_LIMIT) cache.current.delete(cache.current.keys().next().value);
        setState({ key, prices, loading: false, error: Object.values(prices).some((price) => price.source === "error") });
      } catch (error) {
        if (isCurrent()) setState({ key, prices, loading: false, error: true });
      }
    }, 150);
    return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
  }, [key, scope, request, active, identity, businessUnitId, user.id]);

  if (!active) return { prices: {}, loading: false, error: false, retry };
  return { ...(state.key === key ? state : { prices: {}, loading: lines.length > 0, error: false }), retry };
}
